/* eslint-disable no-underscore-dangle */
import type { API } from 'chessboard/api/config/types';
import { ChessError } from 'model/error';
import { createAddMoveToEndOfLine } from 'chessboard/api/add-move-to-end-of-line';
import { createAgreeDraw } from 'chessboard/api/agree-draw';
import { createBlinkHighlight } from 'api/blink-highlight';
import { createCanMoveForward } from 'chessboard/api/can-move-forward';
import { createClaimDraw } from 'chessboard/api/claim-draw';
import { createClearMarkings } from 'api/clear-markings';
import { createCreateContinuation } from 'api/create-continuation';
import { createDeletePosition } from 'chessboard/api/delete-position';
import { EventEmitterUtil } from '@chesscom/web-ui';
import { createExtendAPI } from 'chessboard/api/extend-api';
import { createGetCalculatedResult } from 'chessboard/api/get-calculated-result';
import { createGetContext } from 'chessboard/api/get-context';
import { createGetCurrentFullLine } from 'chessboard/api/get-current-full-line';
import { createGetFen } from 'chessboard/api/get-fen';
import { createGetFingerprints } from 'chessboard/api/get-fingerprints';
import { createGetHeaders } from 'chessboard/api/get-headers';
import { createGetHistoryFENs } from 'chessboard/api/get-history-fens';
import { createGetHistorySANs } from 'chessboard/api/get-history-sans';
import { createGetJCEGameCopy } from 'chessboard/api/get-jce-game-copy';
import { createGetLastMove } from 'chessboard/api/get-last-move';
import { createGetLegalMoves } from 'chessboard/api/get-legal-moves';
import { createGetLegalMovesForSquare } from 'chessboard/api/get-legal-moves-for-square';
import { createGetLine } from 'chessboard/api/get-line';
import { createGetMarkings } from 'chessboard/api/get-markings';
import { createGetMaterial } from 'chessboard/api/get-material';
import { createGetMove } from 'chessboard/api/get-move';
import { createGetNodeByIds } from 'chessboard/api/get-node-by-id';
import { createGetNodeDiffData } from 'chessboard/api/get-node-diff-data';
import { createGetNodeIds } from 'chessboard/api/ids';
import { createGetPieces } from 'chessboard/api/get-pieces';
import { createGetPGN } from 'chessboard/api/get-pgn';
import { createGetPiece } from 'chessboard/api/get-piece';
import { createGetPosition } from 'chessboard/api/get-position';
import { createGetPositionDetails } from 'api/get-position-details';
import { createGetPositionInfo } from 'chessboard/api/get-position-info';
import { createGetRawLines } from 'chessboard/api/get-raw-lines';
import { createGetRelativeNode } from 'chessboard/api/get-relative-node';
import { createGetResult } from 'chessboard/api/get-result';
import { createGetSelectedNode } from 'chessboard/api/get-selected-node';
import { createGetTCN } from 'chessboard/api/get-tcn';
import { createGetTurn } from 'chessboard/api/get-turn';
import { createGetVariant } from 'chessboard/api/get-variant';
import { createIsAtEndOfLine } from 'chessboard/api/is-at-end-of-line';
import { createIsCheck } from 'chessboard/api/is-check';
import { createIsGameOver } from 'chessboard/api/is-game-over';
import { createIsLegalMove } from 'chessboard/api/is-legal-move';
import { createLoad } from 'chessboard/api/load';
import { createMark } from 'chessboard/api/mark';
import { createMemoryCache } from 'src/utils/create-memory-cache';
import { createMove } from 'chessboard/api/move';
import { createMoveBackward } from 'chessboard/api/move-backward';
import { createMoveForward } from 'chessboard/api/move-forward';
import { createMoveVariation } from 'chessboard/api/move-variation';
import { createMutateGame } from 'chessboard/api/mutate-game/create-mutate-game';
import { createOutOfTime } from 'chessboard/api/out-of-time';
import { createOverrideAPI } from 'chessboard/api/override-api';
import { createReload } from 'chessboard/api/reload';
import { createPlayingAsAPI } from 'api/playing-as';
import { createPromoteVariation } from 'chessboard/api/promote-variation';
import { createResetGame } from 'chessboard/api/reset-game';
import { createResign } from 'chessboard/api/resign';
import { createResetToMainLine } from 'chessboard/api/reset-to-main-line';
import { createRespond } from 'chessboard/api/respond/create-respond';
import { createSelectLineEnd } from 'chessboard/api/select-line-end';
import { createSelectLineStart } from 'chessboard/api/select-line-start';
import { createSelectNode } from 'chessboard/api/select-node';
import { createSetGameDetails } from 'chessboard/api/set-game-details';
import { createSetResult } from 'api/set-result';
import { createSetTurn } from 'chessboard/api/set-turn';
import { createToggleMarking } from 'chessboard/api/toggle-marking';
import { createUndo } from 'chessboard/api/undo';
import { createUpdateNode } from 'chessboard/api/update-node';
import { createUpdateLineComment } from 'chessboard/api/update-line-comment';
import { createUnmark } from 'chessboard/api/unmark';
import { createCreateGame } from 'chessboard/api/create-game';
import { createTimeControlAPI } from 'chessboard/api/time-control';
import { createTimestampsAPI } from 'chessboard/api/custom-timestamps';
import { createTimesAPI } from 'chessboard/api/times';
import { createLoggerAPI } from 'chessboard/api/logger';
import { ErrorCodes } from 'model/error/types';
import type { Logger } from 'chessboard/api/logger/config/types';
import JCE from '@chesscom/js-chess-engine';
import { createGetFinalPositionInfo } from './get-final-position-info';

const { createEventEmitter } = EventEmitterUtil;

/**
 * Extension of the JCE game object with an event emitter. See here for JCE game reference:
 * https://github.com/ChessCom/JCE/blob/master/chess-readme.md#game-methods
 */
export function createAPI({
  game: existingGame,
  logger,
  options,
  eventEmitter,
}: {
  game?: JCE.Game;
  logger?: Logger.API;
  options: API.Options;
  eventEmitter?: EventEmitterUtil.EventEmitter;
}): API.ExtendedAPI {
  const game =
    existingGame ||
    JCE.createGame({
      ...options,
      analysis: true,
      autoPromote: true,
    });

  if (!game) {
    throw new ChessError({
      code: ErrorCodes.BadData,
      data: options,
      message: 'Error creating game from createAPI',
    });
  }

  const emitter = eventEmitter || createEventEmitter();
  const markingCache = createMemoryCache({
    arrow: {},
    customItem: {},
    effect: {},
    square: {},
  });
  const apiPlaceholder = <API.ExtendedAPI>{};
  const mutateGame = createMutateGame(game, apiPlaceholder);
  const respond = createRespond(emitter);

  const context: API.Context = {
    api: apiPlaceholder,
    emitter,
    game,
    markingCache,
    mutateGame,
    respond,
  };

  Object.assign(context.api, createAPIModel());

  return context.api;

  /**
   * Define and create the API Model
   */
  function createAPIModel(): API.ExtendedAPI {
    return {
      addMoveToEndOfLine: createAddMoveToEndOfLine(context),
      agreeDraw: createAgreeDraw(context),
      blinkSquare: createBlinkHighlight(context),
      canMoveForward: createCanMoveForward(context),
      claimDraw: createClaimDraw(context),
      clearMarkings: createClearMarkings(context),
      createContinuation: createCreateContinuation(context),
      createGame: createCreateGame(context),
      ...createPlayingAsAPI(context),
      deletePosition: createDeletePosition(context),
      ...emitter,
      extendAPI: createExtendAPI(context),
      overrideAPI: createOverrideAPI(context),
      getCalculatedResult: createGetCalculatedResult(context),
      getContext: createGetContext(context),
      getCurrentFullLine: createGetCurrentFullLine(context),
      getFEN: createGetFen(context),
      getFingerprints: createGetFingerprints(context),
      getHeaders: createGetHeaders(context),
      getHistoryFENs: createGetHistoryFENs(context),
      getHistorySANs: createGetHistorySANs(context),
      getJCEGameCopy: createGetJCEGameCopy(context),
      getLastMove: createGetLastMove(context),
      getLegalMoves: createGetLegalMoves(context),
      getLegalMovesForSquare: createGetLegalMovesForSquare(context),
      getLine: createGetLine(context),
      getMarkings: createGetMarkings(context),
      getMaterial: createGetMaterial(context),
      getMove: createGetMove(context),
      getNodeByIds: createGetNodeByIds(context),
      getNodeDiffData: createGetNodeDiffData(context),
      getNodeIds: createGetNodeIds(context),
      getPGN: createGetPGN(context),
      getPiece: createGetPiece(context),
      getPieces: createGetPieces(context),
      getPosition: createGetPosition(context),
      getPositionDetails: createGetPositionDetails(context),
      getPositionInfo: createGetPositionInfo(context),
      getFinalPositionInfo: createGetFinalPositionInfo(context),
      getRawLines: createGetRawLines(context),
      getRelativeNode: createGetRelativeNode(context),
      getResult: createGetResult(context),
      getSelectedNode: createGetSelectedNode(context),
      getStartingMoveNumber: context.game.startingMoveNumber,
      getTCN: createGetTCN(context),
      getTurn: createGetTurn(context),
      getVariant: createGetVariant(context),
      isAtEndOfLine: createIsAtEndOfLine(context),
      isCheck: createIsCheck(context),
      isGameOver: createIsGameOver(context),
      isLegalMove: createIsLegalMove(context),
      load: createLoad(context),
      logger: logger || createLoggerAPI(),
      mark: createMark(context),
      move: createMove(context),
      moveBackward: createMoveBackward(context),
      moveForward: createMoveForward(context),
      moveVariation: createMoveVariation(context),
      outOfTime: createOutOfTime(context),
      promoteVariation: createPromoteVariation(context),
      reload: createReload(context),
      resetGame: createResetGame(context),
      resetToMainLine: createResetToMainLine(context),
      resign: createResign(context),
      selectLineEnd: createSelectLineEnd(context),
      selectLineStart: createSelectLineStart(context),
      selectNode: createSelectNode(context),
      setGameDetails: createSetGameDetails(context),
      setResult: createSetResult(context),
      setTurn: createSetTurn(context),
      timeControl: createTimeControlAPI(context),
      times: createTimesAPI(context),
      timestamps: createTimestampsAPI(context),
      toggleMarking: createToggleMarking(context),
      undo: createUndo(context),
      unmark: createUnmark(context),
      updateLineComment: createUpdateLineComment(context),
      updateNode: createUpdateNode(context),
    };
  }
}
